import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { LOCAL_STORAGE_KEY } from "app/utils/constants/constants";

const BulkUpdateDialog = ({
  title,
  open,
  onClose,
  onBulkUpdate,
  validateInput,
  successMessage,
  helperText = "Enter an array in JSON format",
}) => {
  const [jsonInput, setJsonInput] = useState("");
  const [error, setError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const handleOpen = () => {
    const savedJson = localStorage.getItem(LOCAL_STORAGE_KEY);
    setJsonInput(savedJson || "");
    setError(null);
  };

  const handleClose = (isSuccess = false) => {
    if (!isSuccess && jsonInput.trim()) {
      localStorage.setItem(LOCAL_STORAGE_KEY, jsonInput);
    }
    setJsonInput("");
    setError(null);
    onClose();
  };

  const handleJsonChange = (e) => {
    setJsonInput(e.target.value);
    setError(null);
  };

  const handleBulkUpdate = async () => {
    try {
      setLoading(true);
      const parsedData = JSON.parse(jsonInput);
      if (!Array.isArray(parsedData)) {
        throw new Error("Input must be a JSON array");
      }

      validateInput(parsedData); // Custom validation from parent
      await onBulkUpdate(parsedData); // Custom update logic from parent

      localStorage.removeItem(LOCAL_STORAGE_KEY);
      setJsonInput("");
      handleClose(true);
    } catch (err) {
      enqueueSnackbar(err.message || "Invalid input data", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => handleClose(false)}
        onOpen={handleOpen}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Paste JSON here"
            multiline
            rows={10}
            fullWidth
            value={jsonInput}
            onChange={handleJsonChange}
            error={!!error}
            helperText={error || helperText}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleClose(false)}
            color="secondary"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkUpdate}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUpdateDialog;
